import type { QueryObserverResult } from '@tanstack/react-query'
import { HttpStatusCode } from 'axios'
import type { AxiosError } from 'axios'

import {
  BasePessoaFisicaUseCase,
  type FetchResult,
} from '@/features/proposta/brasil/domain/Comprador/use-case/base-pessoa-fisica-use-case'
import type { CompradorPessoaJuridicaSchemaInput } from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pj.schema'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import { formatRadios } from '@/features/proposta/brasil/utils/format-radios'
import type { TCliente } from '@/types/cliente'
import { tipoDoVinculoDoCompradorEnum } from '@/types/proposta'
import { origemTelefone } from '@/utils/schemas/telefone.schema'

type TCpfValidatorResponse = { ok: true } | { ok: false; reason: 'REPRESENTANTE_ALREADY_LINKED' }
export class RepresentanteLegalUseCase extends BasePessoaFisicaUseCase<
  CompradorPessoaJuridicaSchemaInput['responsavel']
> {
  constructor(
    private readonly compradores: TCompradoresCadastrados,
    private readonly compradorAtual: CompradorPessoaJuridicaSchemaInput['responsavel'],
    private readonly emailsJaVinculados: Record<string, string>,
    private readonly nacionalidadePrincipalId: string,
  ) {
    super()
  }

  validarCpfOutrosRepresentantes(cpfRaw?: string): TCpfValidatorResponse {
    const comprador = this.compradores.find((c) => c.id === this.compradorAtual?.dados.id)!
    const haVinculosDuplicados = comprador?.vinculos.some(
      (compradorVinculado) =>
        compradorVinculado.tipo === tipoDoVinculoDoCompradorEnum.Representante &&
        this.cpfIgual(compradorVinculado.documento, cpfRaw),
    )
    if (haVinculosDuplicados) return { ok: false, reason: 'REPRESENTANTE_ALREADY_LINKED' }
    return { ok: true }
  }

  interpretarResultadoBuscaManual(
    result: QueryObserverResult<TCliente, AxiosError>,
  ): FetchResult<CompradorPessoaJuridicaSchemaInput['responsavel']> {
    if (result.isError && result.error?.response?.status === HttpStatusCode.NotFound) {
      return { type: 'NOT_FOUND', shouldOpenModal: false }
    }
    if (result.isSuccess && result.data) {
      const cliente = result.data
      return {
        type: 'SUCCESS',
        patch: {
          emailsJaVinculados: this.emailsJaVinculados,
          tipoDoCliente: cliente.tipoDoCliente,
          contato: {
            email: cliente.email ?? '',
            telefone: {
              origem: cliente.telefone?.origem ?? 'Brasil',
              numero: cliente.telefone?.numero ?? '',
              principal: true,
            },
          },
          estadoCivil: {
            status: cliente.estadoCivil ?? null,
            regimeCasamento: cliente.regimeCasamento ?? null,
            uniaoEstavel: formatRadios(cliente.possuiUniaoEstavel),
          },
          dados: {
            id: cliente.id,
            cpf: cliente.cpf ?? '',
            nome: cliente.nome,
            email: cliente.email ?? '',
            profissaoId: cliente.profissao?.id ?? '',
            nacionalidadeId: cliente.nacionalidade?.id ?? this.nacionalidadePrincipalId,
            maiorDe18Anos: 'sim',
            buscouCpf: true,
          },
          imagem: cliente.imagem ?? null,
          tipo: 'Representante',
        },
      }
    }

    return { type: 'ERROR' }
  }

  static iniciarDefaultValues(
    emailsJaVinculados: Record<string, string>,
    nacionalidadePrincipalId?: string,
  ): CompradorPessoaJuridicaSchemaInput['responsavel'] {
    return {
      tipoDoCliente: 'Contato',
      tipo: 'Representante',
      emailsJaVinculados,
      estadoCivil: {
        status: null,
        regimeCasamento: null,
        uniaoEstavel: null,
        tipoAssinatura: null,
      },
      contato: {
        email: '',
        telefone: {
          origem: origemTelefone.Brasil,
          numero: '',
          principal: true,
        },
      },
      dados: {
        cpf: '',
        nome: '',
        email: null,
        profissaoId: '',
        nacionalidadeId: nacionalidadePrincipalId ?? '',
        maiorDe18Anos: 'sim',
        buscouCpf: false,
        id: '',
      },
      imagem: null,
    }
  }

  limparFormulario(): CompradorPessoaJuridicaSchemaInput['responsavel'] {
    return {
      tipoDoCliente: 'Contato',
      emailsJaVinculados: this.emailsJaVinculados,
      estadoCivil: {
        status: null,
        regimeCasamento: null,
        uniaoEstavel: null,
        tipoAssinatura: null,
      },
      contato: {
        email: '',
        telefone: {
          origem: origemTelefone.Brasil,
          numero: '',
          principal: true,
        },
      },
      dados: {
        cpf: '',
        nome: '',
        email: '',
        profissaoId: '',
        nacionalidadeId: this.nacionalidadePrincipalId,
        maiorDe18Anos: 'sim',
        buscouCpf: false,
        id: '',
      },
      imagem: null,
      tipo: tipoDoVinculoDoCompradorEnum.Representante,
    }
  }
}
